import { lineIdOf } from "./variantCore";
/**
 * branchCart: what every website cart line knows about the branch it is
 * bought from.
 * ---------------------------------------------------------------------------
 * Branch operations (4 Oct 2026). A branch may have something sold out,
 * switched off or priced differently from head office, so a cart line keeps
 * the price the server showed and the location it showed it at:
 *
 *   outletId     the branch (absent for the vendor's main location), the
 *                field every checkout already sends
 *   needsCheck   true until the line is confirmed at that location
 *   branchStatus null | LineVerdict status from the last check
 *   quotedPrice  the location's price when the check found it changed
 *
 * The website never works out a branch price. A move marks every line
 * needsCheck, a live branch change marks the lines it touches, a cart read
 * back from localStorage is marked as a whole (prices may have moved since
 * it was saved), and the cart check (hooks/useBranchCartCheck, POST
 * /api/catalog/branch-quote) settles them. The server gate at order create
 * stays the authority; these flags only decide when to ask it first.
 *
 * Pure helpers shared by the brand, restaurant and healthcare cart slices.
 * Every helper takes a cart map ({ [key]: line }, what the slices store) or
 * an array, and hands back the same shape. The same rules as the apps'
 * app/store/branchCart.js.
 * ---------------------------------------------------------------------------
 */
import { createAction } from "@reduxjs/toolkit";
import {
  LINE_STATUS,
  lineProductIdOf,
  locationKeyOf,
  outletIdOfKey,
} from "./branchAvailability";

/**
 * A live `branchProduct:changed` from catalog:public. The storefront slices
 * patch what is on screen; the cart slices mark the lines it touches.
 * Payload: { vertical, parentId, vendorUserId, location, productId,
 *            orderable, reason, soldOutUntil, priceChanged, at }
 */
export const branchProductChanged = createAction("branch/productChanged");

const isRecord = (value) => Boolean(value) && typeof value === "object";
const finite = (value) =>
  value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));

/** Every line of a cart map or array, as an array. */
export const cartLinesOf = (lines) =>
  (Array.isArray(lines) ? lines : Object.values(lines || {})).filter(isRecord);

/* Apply `fn` to every line and hand back the input's shape. `fn` returns the
   SAME line when it has nothing to change, and then so does this, so a slice
   can skip a needless write. */
const mapLines = (lines, fn) => {
  if (Array.isArray(lines)) {
    let changed = false;
    const next = lines.map((line) => {
      const out = isRecord(line) ? fn(line) : line;
      if (out !== line) changed = true;
      return out;
    });
    return changed ? next : lines;
  }
  if (!isRecord(lines)) return lines;
  let changed = false;
  const next = {};
  Object.keys(lines).forEach((key) => {
    const line = lines[key];
    const out = isRecord(line) ? fn(line) : line;
    if (out !== line) changed = true;
    next[key] = out;
  });
  return changed ? next : lines;
};

const CLEAR = Object.freeze({
  needsCheck: false,
  branchStatus: null,
  quotedPrice: null,
  quotedListPrice: null,
});

/** The location a line is bought from: "main" or its outlet id. */
export const lineLocationOf = (line) => locationKeyOf(line?.outletId || null);

/**
 * The location a whole cart is bought from: the first line naming an outlet,
 * else main. One location per vendor cart, so the first one found is the
 * cart's (vendorOutlets.cartOutletId reads it the same way).
 */
export const cartLocationOf = (lines) => {
  const withOutlet = cartLinesOf(lines).find((line) => line.outletId);
  return withOutlet ? String(withOutlet.outletId) : locationKeyOf(null);
};

/**
 * A product as a cart line keeps it: without the storefront's `branch` and
 * `notAt`. Those describe one read at one moment; kept on the line they would
 * later "confirm" it (a + in the cart passes the line back in) after the
 * branch had changed, so only the cart check settles a line.
 */
export const withoutBranchRead = (product) => {
  if (!isRecord(product)) return product;
  const { branch, notAt, ...rest } = product;
  return rest;
};

/**
 * A new line's branch fields. The line is already confirmed only when the
 * product was read for this very location (its `branch` block names it), so
 * an add from search, a deep link or an older server read is checked once
 * before Place order.
 */
export const lineFieldsFor = (product, outletId) => {
  const key = locationKeyOf(outletId || null);
  const readHere =
    isRecord(product?.branch) && locationKeyOf(product.branch.location) === key;
  return { ...CLEAR, needsCheck: !readHere };
};

/**
 * Must this line be confirmed before Place order? A line saved before
 * branches existed has no `needsCheck` at all, so it is checked once.
 */
export const lineNeedsCheck = (line) => isRecord(line) && line.needsCheck !== false;

export const cartNeedsCheck = (lines) => cartLinesOf(lines).some(lineNeedsCheck);

/** Lines the last check flagged and the shopper has not resolved yet. */
export const flaggedLines = (lines) =>
  cartLinesOf(lines).filter(
    (line) => line.branchStatus && line.branchStatus !== LINE_STATUS.OK
  );

/* A line bought from `key` ("main" or an outlet id). */
const atLocation = (line, key) => {
  const next = { ...line };
  const outletId = outletIdOfKey(key);
  if (outletId) next.outletId = outletId;
  else delete next.outletId;
  return next;
};

/**
 * The cart moved to another location (the shopper chose another branch):
 * every line is re-stamped with it and is now priced somewhere it has not
 * been checked, so all of them are confirmed before Place order.
 */
export const markLinesForMove = (lines, outletId) => {
  const key = locationKeyOf(outletId || null);
  return mapLines(lines, (line) => ({
    ...atLocation(line, key),
    needsCheck: true,
    branchStatus: null,
    quotedPrice: null,
    quotedListPrice: null,
  }));
};

/**
 * A cart read back from localStorage: prices and stock may have moved since
 * it was saved, so every line is confirmed again before Place order. The
 * lines keep their location; the last check's flags are kept for the review.
 */
export const markLinesUnchecked = (lines) =>
  mapLines(lines, (line) => (line.needsCheck === true ? line : { ...line, needsCheck: true }));

/**
 * One location per vendor cart. A cart that holds lines from two locations
 * (saved before the website enforced one) is put on the first location found
 * and every line is checked there. A cart already on one location is
 * returned as it is.
 */
export const oneLocationCart = (lines) => {
  const all = cartLinesOf(lines);
  if (!all.length) return lines;
  const keys = new Set(all.map(lineLocationOf));
  return keys.size > 1 ? markLinesForMove(lines, outletIdOfKey(cartLocationOf(lines))) : lines;
};

/**
 * ONE LOCATION PER VENDOR CART, for the slices whose lines name their own
 * branch (restaurant, healthcare). The branch a line being added is bought
 * from: the payload's `outletId` when the caller names one (null is the main
 * branch), else the product's own, else the branch the cart is already on.
 * When that differs from the cart's branch, the cart MOVES there and every
 * line is checked again; the page asks "Move your cart to ...?" before it
 * gets here. Returns the cart to write and the line's outlet id (null for
 * main).
 *
 * @param {object} cart     the vendor's cart map
 * @param {object} payload  the add action's payload: { product, outletId? }
 * @returns {{ cart: object, outletId: string|null }}
 */
export const placeCartLine = (cart, payload = {}) => {
  const current = isRecord(cart) ? cart : {};
  const named = Object.prototype.hasOwnProperty.call(payload || {}, "outletId");
  const raw = named
    ? payload.outletId || null
    : payload?.product?.outletId || outletIdOfKey(cartLocationOf(current));
  const outletId = raw ? String(raw) : null;
  const moves =
    cartLinesOf(current).length > 0 && cartLocationOf(current) !== locationKeyOf(outletId);
  return { cart: moves ? markLinesForMove(current, outletId) : current, outletId };
};

/** Stamp a line (an Immer draft is fine) with its branch; main carries none. */
export const setLineOutlet = (line, outletId) => {
  if (!isRecord(line)) return line;
  if (outletId) line.outletId = String(outletId);
  else delete line.outletId;
  return line;
};

/**
 * A live branch change for one product at one location: the lines it touches
 * are confirmed again before Place order. Returns the SAME cart when no line
 * matched.
 */
export const markLinesChanged = (lines, { productId, location } = {}) => {
  const id = String(productId || "");
  if (!id) return lines;
  const where = location === undefined ? null : locationKeyOf(location);
  return mapLines(lines, (line) => {
    if (lineProductIdOf(line) !== id) return line;
    if (where !== null && lineLocationOf(line) !== where) return line;
    if (line.needsCheck === true) return line;
    return { ...line, needsCheck: true };
  });
};

/**
 * Write the cart check's verdicts onto the lines. OK (and NOT_FOUND, which is
 * its vertical validator's to refuse) settles a line; anything else flags it
 * for the review dialog, with the location's price when that changed. Lines
 * the server did not judge are left as they were.
 */
export const applyQuote = (lines, verdicts) => {
  const byId = new Map(
    (Array.isArray(verdicts) ? verdicts : [])
      .filter((v) => isRecord(v) && v.productId)
      .map((v) => [String(v.lineId || v.productId), v])
  );
  if (!byId.size) return lines;
  return mapLines(lines, (line) => {
    const verdict = byId.get(lineIdOf(line)) || byId.get(lineProductIdOf(line));
    if (!verdict) return line;
    if (verdict.status === LINE_STATUS.OK || verdict.status === LINE_STATUS.NOT_FOUND) {
      return { ...line, ...CLEAR };
    }
    if (verdict.status === LINE_STATUS.PRICE_CHANGED && finite(verdict.unitPrice)) {
      return {
        ...line,
        needsCheck: true,
        branchStatus: LINE_STATUS.PRICE_CHANGED,
        quotedPrice: Number(verdict.unitPrice),
        quotedSpecPriceDelta: finite(verdict.specPriceDelta) ? Number(verdict.specPriceDelta) : null,
        quotedListPrice: finite(verdict.listPrice) ? Number(verdict.listPrice) : null,
      };
    }
    return {
      ...line,
      needsCheck: true,
      branchStatus: verdict.status || LINE_STATUS.SWITCHED_OFF,
      quotedPrice: null,
      quotedListPrice: null,
    };
  });
};

/**
 * "Keep at new price": the line takes the location's price. A list price
 * above it keeps the head office discount showing; otherwise the line has no
 * deal at this location.
 */
export const acceptQuotedPrice = (lines, productId) => {
  const id = String(productId || "");
  return mapLines(lines, (line) => {
    if (lineIdOf(line) !== id && lineProductIdOf(line) !== id) return line;
    if (line.branchStatus !== LINE_STATUS.PRICE_CHANGED || !finite(line.quotedPrice)) return line;
    const price = Number(line.quotedPrice);
    const listPrice =
      finite(line.quotedListPrice) && Number(line.quotedListPrice) > price
        ? Number(line.quotedListPrice)
        : price;
    // The same shape vendorDiscounts.withDealPricing gives a line: a live
    // sale keeps `salePrice`, a line with no deal here carries none.
    const next = { ...line, price, listPrice,
      specPriceDelta: finite(line.quotedSpecPriceDelta) ? Number(line.quotedSpecPriceDelta) : Number(line.specPriceDelta) || 0,
      ...CLEAR };
    if (listPrice > price) {
      next.salePrice = price;
    } else {
      next.salePrice = null;
      next.discount = null;
    }
    return next;
  });
};

/** One quote line for POST /api/catalog/branch-quote. */
export const toQuoteLine = (line) => {
  const out = {
    productId: lineProductIdOf(line),
    ...(line?.lineId || Object.keys(line?.selectedSpecs || {}).length ? { lineId: lineIdOf(line) } : {}),
    ...(line?.specPriceDelta !== undefined ? { specPriceDelta: Number(line.specPriceDelta) || 0 } : {}),
    qty: Number(line?.qty ?? line?.quantity ?? 1) || 1,
    price: finite(line?.price) ? Number(line.price) : null,
  };
  if (Array.isArray(line?.options) && line.options.length) out.options = line.options;
  if (isRecord(line?.selectedSpecs) && Object.keys(line.selectedSpecs).length) {
    out.selectedSpecs = line.selectedSpecs;
  }
  if (line?.name) out.name = line.name;
  return out;
};
